import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Activity, Wifi, ShieldCheck, ArrowLeft, Loader2, Sparkles, CheckCircle2, AlertTriangle, ExternalLink, ClipboardPaste } from 'lucide-react';
import { trackFunnelEvent } from '@/utils/tracking';

const API = '/api';

/**
 * Powersure Device Discovery — standalone pilot/research page (NOT in customer
 * funnel). Implements Blok 5A HomeWizard P1 browser-bridge:
 *
 *   1. Customer types their HomeWizard P1 LAN IP.
 *   2. Browser fetches http://<IP>/api/v1/data directly (CORS-friendly).
 *   3. We extract ONLY summary metrics and POST to Powersure.
 *   4. Powersure never sees the IP.
 *
 * Read-only. No control. No customer-account required.
 */
export default function DeviceDiscoveryPage() {
  const navigate = useNavigate();
  const [instructions, setInstructions] = useState(null);
  const [helperInfo, setHelperInfo] = useState(null);
  const [ip, setIp] = useState('');
  const [ipSuggestion, setIpSuggestion] = useState('');
  const [status, setStatus] = useState('idle'); // idle | fetching | success | error | blocked_mixed_content
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [pastedJson, setPastedJson] = useState('');
  const [pasteError, setPasteError] = useState('');

  useEffect(() => {
    trackFunnelEvent('device_discovery_page_viewed', { meta: { surface: 'pilot_research' } });
    axios.get(`${API}/device-discovery/homewizard/instructions`).then((r) => setInstructions(r.data)).catch(() => {});
    axios.get(`${API}/device-discovery/local-helper/info`).then((r) => setHelperInfo(r.data)).catch(() => {});
  }, []);

  // Detect common IP typo: "192168.2.8" → suggest "192.168.2.8"
  const onIpChange = (val) => {
    const v = val.trim();
    setIp(v);
    setIpSuggestion('');
    if (!v) return;
    const m = v.match(/^(\d{3})(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
    if (m) setIpSuggestion(`${m[1]}.${m[2]}.${m[3]}.${m[4]}`);
  };

  const ipValid = /^(\d{1,3}\.){3}\d{1,3}$/.test(ip);

  // PII-vrije whitelist van velden die naar Powersure mogen.
  // wifi_ssid, unique_id, IP, MAC, serial, host, token, device_id worden NOOIT
  // doorgegeven — we pakken alleen de aggregate metrics die we kennen.
  const _SAFE_FIELDS = ['active_power_w', 'total_power_import_kwh', 'total_power_export_kwh', 'total_gas_m3'];

  const sanitizeAndSubmit = async (raw, sourceLabel) => {
    const payload = {
      session_id: `discovery-${Math.random().toString(36).slice(2, 10)}`,
      active_power_w: Number(raw.active_power_w) || null,
      total_power_import_kwh: Number(raw.total_power_import_kwh ?? raw.total_power_import_t1_kwh ?? raw.total_power_import_t2_kwh) || null,
      total_power_export_kwh: Number(raw.total_power_export_kwh ?? raw.total_power_export_t1_kwh ?? raw.total_power_export_t2_kwh) || null,
      total_gas_m3: Number(raw.total_gas_m3) || null,
      firmware_major: typeof raw.firmware_version === 'string' ? parseInt(raw.firmware_version.split('.')[0], 10) || null : null,
      fetched_at_iso: new Date().toISOString(),
      source: sourceLabel === 'paste_fallback' ? 'paste_fallback' : 'direct_fetch',
    };
    const { data } = await axios.post(`${API}/device-discovery/homewizard/submit-reading`, payload);
    setResult(data);
    setStatus('success');
    trackFunnelEvent('homewizard_bridge_success', { meta: { surface: 'pilot_research', source: sourceLabel } });
  };

  const connect = async () => {
    setStatus('fetching');
    setError('');
    setResult(null);
    try {
      const localUrl = `http://${ip}/api/v1/data`;
      const localRes = await fetch(localUrl, { mode: 'cors', signal: AbortSignal.timeout(6000) });
      if (!localRes.ok) throw new Error(`HomeWizard antwoordde met HTTP ${localRes.status}`);
      const raw = await localRes.json();
      await sanitizeAndSubmit(raw, 'direct_fetch');
    } catch (e) {
      const msg = e?.message || '';
      // Browser blocks the LAN fetch in many forms — mixed content, private
      // network policy, CORS preflight, timeout (Powersure HTTPS pod cannot
      // reach the customer LAN), DNS failure, etc. All of them have the same
      // remedy for the customer: paste the JSON manually. So we route every
      // fetch failure to the same fallback UI.
      const isFetchBlocked = e instanceof TypeError
        || /failed to fetch|networkerror|load failed|fetch failed|cors|insecure|mixed content|private network|signal timed out|timeout|aborted/i.test(msg);
      setError(msg);
      setStatus(isFetchBlocked ? 'blocked_mixed_content' : 'error');
      trackFunnelEvent('homewizard_bridge_error', {
        meta: {
          surface: 'pilot_research',
          reason: isFetchBlocked ? 'fetch_blocked_or_timeout' : 'other',
        },
      });
    }
  };

  const processPasted = async () => {
    setPasteError('');
    if (!pastedJson.trim()) {
      setPasteError('Plak eerst de JSON van uw HomeWizard.');
      return;
    }
    let raw;
    try {
      raw = JSON.parse(pastedJson);
    } catch (e) {
      setPasteError('Geen geldige JSON. Kopieer de volledige tekst uit het browser-tabblad.');
      return;
    }
    if (!raw || typeof raw !== 'object') {
      setPasteError('Geen object gevonden in de JSON.');
      return;
    }
    setStatus('fetching');
    setResult(null);
    try {
      await sanitizeAndSubmit(raw, 'paste_fallback');
    } catch (e) {
      setStatus('error');
      setError(e?.message || 'Indienen mislukt.');
    }
  };

  const openLocalUrlInNewTab = () => {
    if (!ipValid) return;
    window.open(`http://${ip}/api/v1/data`, '_blank', 'noopener,noreferrer');
    trackFunnelEvent('homewizard_local_url_opened', { meta: { surface: 'pilot_research' } });
  };

  return (
    <div className="min-h-screen bg-background py-6 px-4" data-testid="device-discovery-page">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/')} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mb-4">
          <ArrowLeft className="w-3 h-3" /> Terug naar homepage
        </button>

        <div className="bg-white rounded-2xl border border-border p-5 sm:p-7 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-primary" />
            <h1 className="text-xl sm:text-2xl font-medium text-foreground" style={{ fontFamily: 'Outfit' }}>
              Powersure Device Discovery
            </h1>
            <span className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium">Pilot</span>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            Sluit uw HomeWizard P1 meter rechtstreeks aan op Powersure. <strong>Read-only</strong> — wij sturen nooit commando&apos;s, wij ontvangen alleen samengevatte data.
          </p>

          {/* Privacy strip */}
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-[11px] sm:text-xs text-emerald-900 flex items-start gap-2" data-testid="dd-privacy-strip">
            <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Geen IP, geen device-ID, geen klant-account opgeslagen.</strong> Powersure ontvangt alleen samengevatte meting (W, kWh, m³). Uw browser leest direct uit; wij raken uw netwerk nooit aan.
            </div>
          </div>

          {/* Local Device Helper — Primary CTA for HomeWizard + Homey local read */}
          <div className="mt-4 bg-primary/5 border-2 border-primary/30 rounded-lg p-3" data-testid="local-helper-cta">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  <strong className="text-primary text-sm">Powersure Local Device Helper</strong>
                  <span className="text-[9px] uppercase tracking-wider bg-primary/15 text-primary px-1 py-0.5 rounded font-medium">Aanbevolen</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                  Lost browserblokkade (HTTPS↔HTTP / mixed-content / CORS / private network) <strong>volledig</strong> op. Werkt voor HomeWizard P1 én Homey Pro. Geen wachtwoorden of tokens naar Powersure — alles lokaal.
                </div>
              </div>
              <a
                href={`${API}/device-discovery/local-helper/download?v=${helperInfo?.version || '1.6.2'}`}
                download
                className="px-3 py-1.5 text-xs btn-primary rounded-md whitespace-nowrap font-medium"
                data-testid="local-helper-download-btn"
                onClick={() => trackFunnelEvent('local_helper_download_clicked', { meta: { surface: 'pilot_research' } })}
              >
                Download helper
              </a>
            </div>
            {helperInfo && (
              <details className="mt-2 text-[11px]">
                <summary className="cursor-pointer text-muted-foreground hover:text-primary">Hoe gebruik ik de helper? ({helperInfo.requirements?.[0] || 'Python 3.8+'})</summary>
                <ol className="mt-1 list-decimal pl-5 space-y-0.5 text-muted-foreground">
                  {(helperInfo.owner_steps || []).map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </details>
            )}
          </div>

          {/* Instructions */}
          {instructions && (
            <details className="mt-4" data-testid="dd-instructions">
              <summary className="cursor-pointer text-sm font-medium text-foreground hover:text-primary">
                Hoe activeer ik de lokale API op mijn HomeWizard P1? <Sparkles className="inline w-3.5 h-3.5 ml-1" />
              </summary>
              <ol className="mt-2 list-decimal pl-5 space-y-1 text-xs sm:text-sm text-muted-foreground">
                {instructions.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </details>
          )}

          {/* IP input + Connect */}
          <div className="mt-5">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              IP-adres van uw HomeWizard P1
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                inputMode="decimal"
                placeholder="bv. 192.168.2.8"
                value={ip}
                onChange={(e) => onIpChange(e.target.value)}
                className="flex-1 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="dd-ip-input"
              />
              <button
                onClick={connect}
                disabled={!ipValid || status === 'fetching'}
                className="btn-primary px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
                data-testid="dd-connect-btn"
              >
                {status === 'fetching' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
                Verbind
              </button>
            </div>
            {ipSuggestion && (
              <p className="text-[11px] text-amber-700 mt-1" data-testid="dd-ip-suggestion">
                Tikfout? Bedoelde u <button type="button" onClick={() => onIpChange(ipSuggestion)} className="underline text-primary">{ipSuggestion}</button>?
              </p>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">
              Het IP-adres blijft alleen in uw browser. Powersure ontvangt dit niet.
            </p>
          </div>

          {/* Result */}
          {status === 'blocked_mixed_content' && (
            <div className="mt-4 space-y-3" data-testid="dd-blocked-fallback">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="dd-blocked-explanation">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Browserblokkade: Powersure draait op HTTPS, uw HomeWizard op HTTP.</strong> Moderne browsers verbieden die mix. <strong>Gebruik de Powersure Local Device Helper</strong> — die lost dit volledig op.
                </div>
              </div>

              {/* PRIMARY: Local Device Helper */}
              <div className="bg-white border-2 border-primary/50 rounded-lg p-3" data-testid="dd-blocked-helper-cta">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-primary" />
                      <strong className="text-primary text-sm">Aanbevolen route — Local Device Helper</strong>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Python-scriptje dat lokaal op uw computer draait en de HomeWizard veilig uitleest. Geen plakwerk, geen tokens.</div>
                  </div>
                  <a
                    href={`${API}/device-discovery/local-helper/download?v=${helperInfo?.version || '1.6.2'}`}
                    download
                    className="px-3 py-2 text-xs btn-primary rounded-md whitespace-nowrap font-medium"
                    data-testid="dd-blocked-helper-download-btn"
                    onClick={() => trackFunnelEvent('local_helper_download_clicked', { meta: { surface: 'pilot_research', from: 'blocked_state' } })}
                  >
                    Download helper
                  </a>
                </div>
              </div>

              {/* SECONDARY (advanced): paste fallback */}
              <details className="bg-white border border-border rounded-lg p-3" data-testid="dd-blocked-paste-details">
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-primary">Geavanceerde route — plak-fallback (alleen als helper niet kan)</summary>
                <ol className="list-decimal pl-4 space-y-2 text-xs mt-2">
                  <li className="space-y-1">
                    <span>Klik hieronder om de HomeWizard data in een nieuw tabblad te openen:</span>
                    <button
                      onClick={openLocalUrlInNewTab}
                      className="mt-1 w-full sm:w-auto px-3 py-2 bg-primary text-white rounded-md text-xs font-medium flex items-center gap-1.5"
                      data-testid="dd-open-local-btn"
                    >
                      <ExternalLink className="w-3 h-3" /> Open lokale HomeWizard data
                    </button>
                  </li>
                  <li>Selecteer alles in dat tabblad (Ctrl/Cmd + A) en kopieer (Ctrl/Cmd + C).</li>
                  <li>
                    Plak de JSON hieronder en klik &laquo;Verwerk meting&raquo;:
                    <textarea
                      value={pastedJson}
                      onChange={(e) => setPastedJson(e.target.value)}
                      rows={6}
                      placeholder='{"smr_version": 50, "active_power_w": 250, ...}'
                      className="mt-2 w-full border border-border rounded-md px-2 py-1.5 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="dd-paste-textarea"
                    />
                    {pasteError && (
                      <p className="text-[11px] text-rose-700 mt-1" data-testid="dd-paste-error">{pasteError}</p>
                    )}
                    <button
                      onClick={processPasted}
                      disabled={!pastedJson.trim() || status === 'fetching'}
                      className="mt-2 btn-primary px-3 py-2 rounded-md text-xs font-medium disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="dd-process-paste-btn"
                    >
                      {status === 'fetching' ? <Loader2 className="w-3 h-3 animate-spin" /> : <ClipboardPaste className="w-3 h-3" />}
                      Verwerk meting
                    </button>
                  </li>
                </ol>
                <p className="text-[10px] text-muted-foreground mt-3 italic">
                  Powersure haalt alleen <code>active_power_w</code>, <code>total_power_import_kwh</code>, <code>total_power_export_kwh</code>, <code>total_gas_m3</code> uit uw plakwerk. <strong>wifi_ssid, unique_id, IP, MAC, serial</strong> en alle andere velden worden in uw browser <em>vóór verzending</em> weggegooid.
                </p>
              </details>
            </div>
          )}
          {status === 'error' && (
            <div className="mt-4 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-900 flex items-start gap-2" data-testid="dd-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Verbinding mislukt.</strong> {error}
                <ul className="mt-1 list-disc pl-4">
                  <li>Klopt het IP-adres?</li>
                  <li>Staat &laquo;Lokale API&raquo; aan in de HomeWizard Energy app?</li>
                  <li>Zit u op hetzelfde netwerk als uw P1 meter?</li>
                </ul>
              </div>
            </div>
          )}
          {status === 'success' && result && (
            <div className="mt-4" data-testid="dd-success">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-900 flex items-start gap-2 mb-3">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div><strong>Verbonden.</strong> {result.notice}</div>
              </div>

              {/* Power tile — sign-aware: import (verbruik) vs export (teruglevering) */}
              {(() => {
                const w = result.reading.active_power_w;
                let label, value, microcopy, tone;
                const fmt = (n) => Math.round(Math.abs(n)).toLocaleString('nl-NL');
                if (w == null || w === 0) {
                  label = 'Vermogen nu';
                  value = '—';
                  microcopy = '';
                  tone = 'bg-muted/40 text-foreground';
                } else if (w < 0) {
                  label = 'Teruglevering nu';
                  value = `${fmt(w)} W`;
                  microcopy = 'U levert op dit moment stroom terug aan het net.';
                  tone = 'bg-emerald-50 border border-emerald-200 text-emerald-900';
                } else {
                  label = 'Verbruik nu';
                  value = `${fmt(w)} W`;
                  microcopy = 'Uw woning verbruikt op dit moment stroom.';
                  tone = 'bg-blue-50 border border-blue-200 text-blue-900';
                }
                return (
                  <div className={`rounded-lg p-3 mb-3 ${tone}`} data-testid="dd-power-tile">
                    <div className="text-[10px] uppercase tracking-wider opacity-80" data-testid="dd-power-label">{label}</div>
                    <div className="text-2xl font-medium mt-0.5" data-testid="dd-power-value">{value}</div>
                    {microcopy && (
                      <div className="text-[11px] mt-1 opacity-90" data-testid="dd-power-microcopy">{microcopy}</div>
                    )}
                  </div>
                );
              })()}

              {/* Counter tiles — import / export / gas */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { label: 'Import totaal', value: result.reading.total_power_import_kwh, unit: 'kWh' },
                  { label: 'Export totaal', value: result.reading.total_power_export_kwh, unit: 'kWh' },
                  { label: 'Gas totaal', value: result.reading.total_gas_m3, unit: 'm³' },
                ].map((m) => (
                  <div key={m.label} className="bg-muted/40 rounded p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.label}</div>
                    <div className="text-lg font-medium">{m.value != null ? Number(m.value).toLocaleString('nl-NL') : '—'} <span className="text-xs text-muted-foreground">{m.unit}</span></div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-900">
                <strong>Powersure-advies (read-only):</strong>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  {(result.advice || []).map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            </div>
          )}

          <div className="mt-6 text-[10px] text-muted-foreground italic border-t border-border pt-3">
            Pilot-pagina — niet onderdeel van de standaard quickscan. Geen device-control. Geen flows. Geen schakelen.
          </div>

          {/* Blok 5B Phase 1C — Homey Local Browser Bridge */}
          <HomeyLocalBridgeSection />
        </div>
      </div>
    </div>
  );
}

/**
 * Homey Local Browser Bridge — read-only device + capability discovery.
 * The OWNER's browser calls the Homey local API directly (or pastes JSON
 * fallback if mixed-content blocks). Backend receives ONLY the safe
 * aggregate — never the PAT, IP, MAC, serial, or unique device IDs.
 */
function HomeyLocalBridgeSection() {
  const [ip, setIp] = useState('');
  const [pat, setPat] = useState('');
  const [pasted, setPasted] = useState('');
  const [status, setStatus] = useState('idle'); // idle | fetching | success | error
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const ipValid = /^(\d{1,3}\.){3}\d{1,3}$/.test(ip);

  const ENERGY_CAPS = ['measure_power', 'meter_power', 'measure_voltage', 'measure_current', 'measure_battery', 'measure_temperature'];

  // Same mapping as backend _classify_homey_device — mirrored client-side
  const CLASS_MAP = {
    sensor:          { cat: 'A_p1_meter',    safety: 'read_only', label: 'Sensor / meter' },
    solarpanel:      { cat: 'D_inverters',   safety: 'read_only', label: 'Zonnepaneel' },
    battery:         { cat: 'E_batteries',   safety: 'limited_control_later', label: 'Batterij' },
    socket:          { cat: 'B_smart_plugs', safety: 'limited_control_later', label: 'Smart plug' },
    thermostat:      { cat: 'G_climate',     safety: 'limited_control_later', label: 'Thermostaat' },
    heatpump:        { cat: 'G_climate',     safety: 'installer_required', label: 'Warmtepomp' },
    heater:          { cat: 'G_climate',     safety: 'installer_required', label: 'Verwarming' },
    airconditioning: { cat: 'G_climate',     safety: 'limited_control_later', label: 'Airco' },
    fan:             { cat: 'G_climate',     safety: 'read_only', label: 'Ventilator' },
    evcharger:       { cat: 'F_ev',          safety: 'limited_control_later', label: 'EV-lader' },
    kettle:          { cat: 'H_boiler',      safety: 'read_only', label: 'Waterkoker' },
    light:           { cat: 'B_smart_plugs', safety: 'read_only', label: 'Verlichting' },
    other:           { cat: '_other',        safety: 'unknown', label: 'Onbekend' },
  };

  function classifyDevice(d) {
    const cls = String(d.class || 'other').toLowerCase();
    const m = CLASS_MAP[cls] || CLASS_MAP.other;
    const caps = Array.isArray(d.capabilities) ? d.capabilities : [];
    const energy = caps.filter((c) => ENERGY_CAPS.includes(c));
    return {
      name: (d.name || '(zonder naam)').slice(0, 64),
      homey_class: cls.slice(0, 32),
      capability_count: caps.length,
      energy_capabilities: energy.slice(0, 12),
      powersure_category: m.cat,
      safety_class: m.safety,
      label: m.label,
    };
  }

  function buildAggregate(rawDevices) {
    const arr = Array.isArray(rawDevices) ? rawDevices : Object.values(rawDevices || {});
    const mapped = arr.filter((d) => d && typeof d === 'object').map(classifyDevice);
    const by_category = {};
    const by_safety_class = {};
    const cap_summary = {};
    let energy_relevant = 0;
    let live_total_power = null;
    for (const d of mapped) {
      by_category[d.powersure_category] = (by_category[d.powersure_category] || 0) + 1;
      by_safety_class[d.safety_class] = (by_safety_class[d.safety_class] || 0) + 1;
      if (d.energy_capabilities.length) energy_relevant += 1;
      for (const c of d.energy_capabilities) cap_summary[c] = (cap_summary[c] || 0) + 1;
    }
    // Live power: sum measure_power if present in raw capabilitiesObj
    try {
      let sum = 0; let any = false;
      for (const d of arr) {
        const v = d?.capabilitiesObj?.measure_power?.value;
        if (typeof v === 'number') { sum += v; any = true; }
      }
      if (any) live_total_power = Math.round(sum);
    } catch (_) {}
    return {
      device_count: mapped.length,
      energy_relevant_count: energy_relevant,
      by_category,
      by_safety_class,
      mapped_devices: mapped.map(({ label: _label, ...rest }) => rest),
      capability_summary: cap_summary,
      live_total_power_w: live_total_power,
      homey_model_label: 'Homey Pro (local bridge)',
    };
  }

  async function tryDirectFetch() {
    if (!ipValid) { setError('Voer een geldig lokaal IPv4 adres in'); return; }
    if (!pat || pat.length < 8) { setError('PAT/API-key is te kort'); return; }
    setStatus('fetching'); setError(''); setResult(null);
    // We try both http and https — Homey Pro Early 2023 exposes https local API
    const candidates = [`https://${ip}/api/manager/devices/device/`, `http://${ip}/api/manager/devices/device/`];
    for (const url of candidates) {
      try {
        const r = await fetch(url, { headers: { Authorization: `Bearer ${pat}` } });
        if (!r.ok) { setError(`Homey gaf HTTP ${r.status} op ${url.split('://')[0]}://… — controleer PAT/rechten`); continue; }
        const raw = await r.json();
        await submitAggregate(buildAggregate(raw), 'direct');
        return;
      } catch (e) {
        // Mixed-content / CORS / network — try next or fall through
      }
    }
    setStatus('error');
    setError('Browser kon Homey lokaal niet bereiken (mixed-content of CORS). Gebruik de paste-fallback hieronder.');
  }

  async function tryPaste() {
    setError(''); setResult(null);
    try {
      const raw = JSON.parse(pasted);
      setStatus('fetching');
      await submitAggregate(buildAggregate(raw), 'paste');
    } catch (_e) {
      setStatus('error');
      setError('Ongeldige JSON. Plak de volledige response van /api/manager/devices/device/');
    }
  }

  async function submitAggregate(agg, mode) {
    try {
      const { data } = await axios.post(`${API}/device-discovery/homey-local-bridge/submit-mapping`, agg);
      setResult({ ...agg, snapshot_id: data.snapshot_id, mode });
      setStatus('success');
      setPat(''); setPasted(''); // wipe sensitive inputs from DOM
    } catch (e) {
      setStatus('error');
      setError(e?.response?.data?.detail || 'Verzenden naar Powersure mislukt');
    }
  }

  return (
    <div className="mt-8 border-t border-border pt-6" data-testid="homey-local-bridge">
      <h2 className="text-base font-medium flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-primary" /> Homey Pro lokaal uitlezen — pilot
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">READ-ONLY</span>
      </h2>
      <p className="text-xs text-muted-foreground mb-3">
        Cloud-route via Athom OAuth/PAT bleek geblokkeerd voor dit account. Deze lokale browser-bridge leest je Homey direct uit (browser → lokale Homey-IP) en stuurt alleen veilige geaggregeerde mapping naar Powersure. <strong>Geen PAT, IP, MAC of serial wordt opgeslagen.</strong>
      </p>

      <div className="bg-primary/5 border border-primary/30 rounded p-3 mb-3" data-testid="homey-helper-cta">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <strong className="text-primary">Aanbevolen: Powersure Local Device Helper</strong>
            <div className="text-[11px] text-muted-foreground">Python-helper die lokaal op uw machine draait — omzeilt mixed-content/CORS/private-network browser-blokkades volledig. Werkt voor HomeWizard P1 én Homey Pro.</div>
          </div>
          <a href={`${API}/device-discovery/local-helper/download?v=1.6.2`} download className="px-3 py-1.5 text-xs btn-primary rounded-md whitespace-nowrap" data-testid="homey-helper-download-btn">Download helper</a>
        </div>
      </div>

      <ol className="text-[11px] text-muted-foreground list-decimal pl-5 mb-3 space-y-0.5">
        <li>Revoke je oude PAT (prefix <code>cf0d</code>) op <a href="https://my.homey.app/me" target="_blank" rel="noopener" className="text-primary underline">my.homey.app/me</a></li>
        <li>Maak een nieuwe Personal Access Token aan met <strong>uitsluitend read-only</strong> scopes (`devices.readonly`, `energy.readonly`)</li>
        <li>Zoek je lokale Homey IP (Homey app → Settings → System info)</li>
        <li>Vul IP + PAT hieronder in en klik "Test lokaal uitlezen"</li>
      </ol>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
        <input type="text" value={ip} onChange={(e) => setIp(e.target.value)} placeholder="Homey lokaal IP (bv. 192.168.1.50)" className="text-xs border border-border rounded px-2 py-1.5" data-testid="homey-local-ip" />
        <input type="password" value={pat} onChange={(e) => setPat(e.target.value)} placeholder="PAT (read-only)" className="text-xs border border-border rounded px-2 py-1.5" data-testid="homey-local-pat" autoComplete="off" />
        <button onClick={tryDirectFetch} disabled={status === 'fetching' || !ipValid} className="px-3 py-1.5 text-xs btn-primary rounded-md flex items-center justify-center gap-1" data-testid="homey-local-test">
          {status === 'fetching' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wifi className="w-3 h-3" />} Test lokaal uitlezen
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded p-2 text-[11px] text-rose-900 mb-2" data-testid="homey-local-error">
          <AlertTriangle className="w-3 h-3 inline mr-1" /> {error}
          <div className="mt-2 bg-white border border-primary/30 rounded p-2">
            <strong className="text-primary">Aanbevolen: gebruik de Powersure Local Device Helper</strong>
            <div className="text-[10px] text-muted-foreground mt-0.5">Python-helper omzeilt mixed-content/CORS/private-network. Download, run, vul IP + PAT in, klik Uitlezen, klik Verstuur.</div>
            <a href={`${API}/device-discovery/local-helper/download?v=1.6.2`} download className="inline-block mt-1 px-2.5 py-1 text-xs btn-primary rounded-md" data-testid="homey-helper-link">Download Local Device Helper</a>
          </div>
        </div>
      )}

      {/* Paste fallback */}
      <details className="text-[11px] mb-2">
        <summary className="cursor-pointer text-amber-700">Paste-fallback (als browser de directe call blokkeert)</summary>
        <div className="mt-2 space-y-2">
          <p className="text-muted-foreground">Open in een nieuw tabblad: <code>https://[IP]/api/manager/devices/device/</code> met Authorization Bearer header (bijv. via curl) en plak de JSON hieronder:</p>
          <textarea value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder='{"device-id-1": {"name":"...", "class":"...", "capabilities":[...]}, ...}' className="w-full text-[10px] border border-border rounded px-2 py-1 font-mono" rows={5} data-testid="homey-local-paste" />
          <button onClick={tryPaste} disabled={!pasted.trim()} className="px-3 py-1.5 text-xs border border-primary text-primary rounded-md flex items-center gap-1" data-testid="homey-local-paste-submit">
            <ClipboardPaste className="w-3 h-3" /> Verwerk mapping
          </button>
        </div>
      </details>

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs space-y-2" data-testid="homey-local-result">
          <div className="flex items-center justify-between flex-wrap gap-1">
            <strong className="text-emerald-900">Source: Homey local bridge ({result.mode})</strong>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900">Read-only — geen sturing</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><div className="text-[10px] uppercase tracking-wider text-emerald-700">Devices</div><div className="text-lg font-medium text-emerald-900">{result.device_count}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-emerald-700">Energie-relevant</div><div className="text-lg font-medium text-emerald-900">{result.energy_relevant_count}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-emerald-700">Categorieën</div><div className="text-lg font-medium text-emerald-900">{Object.keys(result.by_category || {}).length}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-emerald-700">Live W</div><div className="text-lg font-medium text-emerald-900">{result.live_total_power_w ?? '—'}</div></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px]">
              <thead className="text-muted-foreground bg-white/60">
                <tr><th className="text-left px-1 py-1">Naam</th><th className="text-left px-1">Class</th><th className="text-left px-1">Categorie</th><th className="text-left px-1">Safety</th><th className="text-left px-1 hidden sm:table-cell">Energie caps</th></tr>
              </thead>
              <tbody>
                {(result.mapped_devices || []).map((d, i) => (
                  <tr key={i} className="border-t border-emerald-100">
                    <td className="px-1 py-0.5 font-medium">{d.name}</td>
                    <td className="px-1 py-0.5 text-muted-foreground">{d.homey_class}</td>
                    <td className="px-1 py-0.5">{d.powersure_category}</td>
                    <td className="px-1 py-0.5"><span className="bg-white px-1 rounded border border-emerald-200">{d.safety_class}</span></td>
                    <td className="px-1 py-0.5 hidden sm:table-cell text-muted-foreground">{(d.energy_capabilities || []).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-[10px] text-muted-foreground italic mt-2">
        Verzonden naar Powersure: alleen geaggregeerde mapping (geen PAT, geen IP, geen MAC, geen serial, geen unieke device-id's). Browser-memory only voor PAT — leeggemaakt na submit. Bestaande HomeWizard P1 pilot blijft werken.
      </div>
    </div>
  );
}
